import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, CheckCircle2, Loader2, ShieldCheck, Sparkles,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORY_KEYS = ["coaching_institute", "independent_counsellor", "engineering_college", "other"];

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 72;
  window.scrollTo({ top, behavior: "smooth" });
};

export default function Partners() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <Seo
        path="/partners"
        title="Partner Programme · For Counsellors & Coaching Institutes"
        description="Join the AdmissionCrafts partner programme for independent counsellors, coaching institutes and colleges. An opportunity to grow together — apply in two minutes."
      />
      <Navbar
        onOpenAuth={() => scrollToId("partner-form")}
        onOpenSignIn={() => (window.location.href = "/portal/login")}
      />

      <PartnersHero onApply={() => (window.location.href = "/partner-signup")} />
      <PartnersHow />
      <PartnersWhy />

      <section className="bg-white border-b border-slate-200" data-testid="partners-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-amber-700">Partner FAQ</p>
          <h2 className="mt-3 font-display font-bold text-slate-900 tracking-tight text-3xl sm:text-4xl leading-[1.1]">
            Everything you might be wondering
          </h2>
          <div className="mt-8 space-y-8">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">Who is this programme for?</h3>
              <p className="mt-2 text-[15px] text-slate-700 leading-relaxed">
                Independent counsellors, coaching institutes running entrance-exam batches, college outreach
                cells, and school career-guidance teams. If you regularly advise students on admissions
                across India, our partner code makes it easy to bring them a data-grounded shortlist
                alongside your own advice.
              </p>
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">Do I need a registered business to apply?</h3>
              <p className="mt-2 text-[15px] text-slate-700 leading-relaxed">
                No — we onboard both registered entities (proprietorships, LLPs, private limited companies) and
                individual counsellors. All we need is a PAN, an active bank account for commission payouts, and a
                mobile number that can receive an OTP. Everything is verified inside the partner portal after signup.
              </p>
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">How do commissions work?</h3>
              <p className="mt-2 text-[15px] text-slate-700 leading-relaxed">
                Every ₹99 prediction sold with your unique partner code earns you a transparent share. Balances
                accumulate in real time in your partner dashboard, and payouts are settled every calendar month to
                the bank account you verify during KYC. You receive an SMS with the UTR the moment the payout is
                released.
              </p>
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">What does AdmissionCrafts actually deliver to my students?</h3>
              <p className="mt-2 text-[15px] text-slate-700 leading-relaxed">
                A personalised college shortlist, grouped as High Chance, Possible Chance, and Low Chance, based
                on the student&apos;s marks, category and preferences. The engine draws from the official past-year
                cutoffs of every exam we&apos;re live for (MHT-CET CAP, NEET MCC/State DME) and refreshes each cycle
                when the counselling authorities publish new numbers.
              </p>
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">How long does approval take?</h3>
              <p className="mt-2 text-[15px] text-slate-700 leading-relaxed">
                Most KYC reviews are completed within 24 to 48 hours. Once your unique partner code is activated you
                will receive both an email and an SMS, and you can start sharing the code with students immediately.
              </p>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link to="/partner-signup">
              <Button className="h-12 px-8 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold" data-testid="partners-faq-cta">
                Start your partner signup <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section id="partner-form" className="bg-[#FAFAFA] border-b border-slate-200 scroll-mt-20" data-testid="section-partner-form">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-amber-700">
            {t("partners.form.eyebrow")}
          </p>
          <h2 className="mt-3 font-display font-bold text-slate-900 tracking-tight text-3xl sm:text-4xl leading-[1.1]">
            {t("partners.form.title")}
          </h2>
          <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-xl mx-auto">
            Sign up in 60 seconds with your email and mobile. Then submit KYC inside the partner portal — your unique partner code is issued once we verify your details (typically within 24-48 hours).
          </p>

          <Link to="/partner-signup">
            <Button className="mt-8 h-12 px-8 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold" data-testid="partner-page-signup-cta">
              Create your partner account <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>

          <p className="text-[11px] text-slate-500 mt-5">
            Already a partner? <Link to="/portal/login" className="text-slate-900 font-semibold underline" data-testid="partner-page-signin-link">Sign in here</Link>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function PartnersHero({ onApply }) {
  const { t } = useTranslation();
  return (
    <section className="bg-white border-b border-slate-200" data-testid="partners-hero">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-amber-700">
          {t("partners.eyebrow")}
        </p>
        <h1 className="mt-3 font-display font-bold text-slate-900 tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">
          {t("partners.title")}
        </h1>
        <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-3xl">
          {t("partners.subtitle")}
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Button
            onClick={onApply}
            className="h-12 px-6 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold"
            data-testid="partners-cta-apply"
          >
            {t("partners.ctaApply")} <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
          <Link
            to="/portal/login"
            className="h-12 px-6 rounded-full border border-slate-300 bg-white hover:bg-slate-50 text-slate-900 text-sm font-semibold flex items-center"
            data-testid="partners-cta-signin"
          >
            {t("partners.ctaSignin")}
          </Link>
        </div>

        <div className="mt-12 max-w-3xl border border-amber-200 bg-amber-50/60 rounded-xl px-6 py-5 flex items-center gap-3" data-testid="partners-tagline">
          <Sparkles className="w-5 h-5 text-amber-700 flex-shrink-0" />
          <p className="text-base sm:text-lg text-slate-900 font-display font-semibold tracking-tight">
            {t("partners.tagline")}
          </p>
        </div>
      </div>
    </section>
  );
}

function PartnersHow() {
  const { t } = useTranslation();
  const steps = ["s1", "s2", "s3"];
  return (
    <section className="bg-[#FAFAFA] border-b border-slate-200" data-testid="partners-how">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-amber-700">
          {t("partners.how.eyebrow")}
        </p>
        <h2 className="mt-3 font-display font-bold text-slate-900 tracking-tight text-3xl sm:text-4xl leading-[1.1] max-w-3xl">
          {t("partners.how.title")}
        </h2>
        <ol className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <li key={s} className="border border-slate-200 rounded-xl p-6 bg-white" data-testid={`partners-how-${s}`}>
              <div className="font-display tabular-nums text-amber-700 text-3xl font-bold">0{i + 1}</div>
              <h3 className="mt-3 font-display text-lg font-bold text-slate-900">{t(`partners.how.${s}Title`)}</h3>
              <p className="mt-2 text-[14px] text-slate-600 leading-relaxed">{t(`partners.how.${s}Body`)}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function PartnersWhy() {
  const { t } = useTranslation();
  const items = ["p1", "p2", "p3"];
  return (
    <section className="bg-white border-b border-slate-200" data-testid="partners-why">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-amber-700">
          {t("partners.why.eyebrow")}
        </p>
        <h2 className="mt-3 font-display font-bold text-slate-900 tracking-tight text-3xl sm:text-4xl leading-[1.1] max-w-3xl">
          {t("partners.why.title")}
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((k) => (
            <article key={k} className="border border-slate-200 rounded-xl p-6 bg-white" data-testid={`partners-why-${k}`}>
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              <h3 className="mt-3 font-display text-lg font-bold text-slate-900">{t(`partners.why.${k}Title`)}</h3>
              <p className="mt-2 text-[14px] text-slate-600 leading-relaxed">{t(`partners.why.${k}Body`)}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function RegisterInterestForm() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", city: "",
    category: "independent_counsellor",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const valid = () =>
    form.full_name.trim().length > 2 &&
    /@/.test(form.email) &&
    form.phone.length >= 8 &&
    form.city.length >= 2 &&
    CATEGORY_KEYS.includes(form.category);

  const submit = async () => {
    if (!valid()) {
      toast.error(t("partners.form.errorRequired"));
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/public/partner/apply`, {
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        category: form.category,
        notes: form.notes.trim() || null,
      });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <Card className="mt-8 p-8 sm:p-10 text-center bg-white border border-emerald-200" data-testid="partner-apply-done">
        <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
        <h3 className="font-display text-2xl font-bold mt-4 text-slate-900">{t("partners.form.doneTitle")}</h3>
        <p className="text-sm text-slate-600 mt-2" data-testid="partner-apply-done-email">
          {t("partners.form.doneBody", { email: form.email })}
        </p>
        <Link to="/" className="mt-6 inline-flex items-center text-sm text-slate-900 underline" data-testid="partner-apply-back">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> {t("partners.form.doneBack")}
        </Link>
      </Card>
    );
  }

  return (
    <Card className="mt-8 p-6 sm:p-8 space-y-6 bg-white border border-slate-200" data-testid="partner-apply-form">
      <Section title={t("partners.form.section1")}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("partners.form.fullName")}><Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder={t("partners.form.fullNamePh")} data-testid="apply-full-name" /></Field>
          <Field label={t("partners.form.email")}><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder={t("partners.form.emailPh")} data-testid="apply-email" /></Field>
          <Field label={t("partners.form.phone")}><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder={t("partners.form.phonePh")} data-testid="apply-phone" /></Field>
          <Field label={t("partners.form.city")}><Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder={t("partners.form.cityPh")} data-testid="apply-city" /></Field>
        </div>
      </Section>

      <Section title={t("partners.form.section2")}>
        <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="apply-category-group">
          {CATEGORY_KEYS.map((k) => (
            <label
              key={k}
              className={`flex items-center gap-3 px-4 h-12 rounded-lg border cursor-pointer transition-colors ${
                form.category === k
                  ? "border-slate-900 bg-slate-50"
                  : "border-slate-300 bg-white hover:bg-slate-50"
              }`}
              data-testid={`apply-category-${k}`}
            >
              <input
                type="radio"
                name="partner-category"
                value={k}
                checked={form.category === k}
                onChange={(e) => set("category", e.target.value)}
                className="accent-slate-900"
              />
              <span className="text-sm font-medium text-slate-900">
                {t(`partners.form.categoryOptions.${k}`)}
              </span>
            </label>
          ))}
        </fieldset>
        <p className="mt-2 text-[11px] text-slate-500">{t("partners.form.categoryHelp")}</p>
      </Section>

      <Section title={t("partners.form.section5")}>
        <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} placeholder={t("partners.form.notes")} data-testid="apply-notes" />
      </Section>

      <div className="flex justify-end pt-2">
        <Button
          onClick={submit}
          disabled={busy || !valid()}
          className="h-12 px-6 bg-slate-900 hover:bg-slate-800 rounded-full"
          data-testid="apply-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          {busy ? t("partners.form.submitBusy") : t("partners.form.submit")}
        </Button>
      </div>
    </Card>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="font-display font-semibold text-slate-900">{title}</h3>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] tracking-[0.15em] uppercase text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
